import type { DosageForm } from '@/api/medications';

// Labels mirror es-CL `medications.vocabulary.routes` (the server validates the keys).
export const ROUTES = [
  { value: 'oral', title: 'Oral' },
  { value: 'sublingual', title: 'Sublingual' },
  { value: 'enteral', title: 'Enteral (sonda)' },
  { value: 'intravenous', title: 'Intravenosa' },
  { value: 'intramuscular', title: 'Intramuscular' },
  { value: 'subcutaneous', title: 'Subcutánea' },
  { value: 'inhalation', title: 'Inhalatoria' },
  { value: 'nasal', title: 'Nasal' },
  { value: 'transdermal', title: 'Transdérmica' },
  { value: 'topical', title: 'Tópica' },
  { value: 'ophthalmic', title: 'Oftálmica' },
  { value: 'otic', title: 'Ótica' },
  { value: 'rectal', title: 'Rectal' },
  { value: 'vaginal', title: 'Vaginal' },
] as const;

// Ruby wday order: 0 = Sunday. Shown Monday first.
export const WEEKDAYS = [
  { value: 1, title: 'Lun' },
  { value: 2, title: 'Mar' },
  { value: 3, title: 'Mié' },
  { value: 4, title: 'Jue' },
  { value: 5, title: 'Vie' },
  { value: 6, title: 'Sáb' },
  { value: 0, title: 'Dom' },
] as const;

export function routeLabel(route: string): string {
  return ROUTES.find((option) => option.value === route)?.title ?? route;
}

const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const TWO_DIGITS = 2;
const MINUTES_PER_DAY = 1440;
const MS_PER_DAY = 86_400_000;
const DAYS_PER_WEEK = 7;

export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  const rest = minutes % MINUTES_PER_HOUR;

  return `${String(hours).padStart(TWO_DIGITS, '0')}:${String(rest).padStart(TWO_DIGITS, '0')}`;
}

// A clock time typed on the 24-hour clock, the way people jot it down: "8",
// "800", "08:30", "20.30" or "20h30". Null when it is not a time of day.
export function parseTime(text: string): number | null {
  const match = /^(?<hours>\d{1,2})(?:[:.h]?(?<rest>\d{2}))?$/.exec(text.trim());
  if (!match?.groups) return null;
  const hours = Number(match.groups.hours);
  const rest = Number(match.groups.rest ?? 0);

  return hours < HOURS_PER_DAY && rest < MINUTES_PER_HOUR ? hours * MINUTES_PER_HOUR + rest : null;
}

// Clock times in the order they are given from the first dose: every 8 hours
// from 08:00 reads 08:00 → 16:00 → 00:00.
export function fromFirst(times: number[], first: number): number[] {
  const sorted = [...times].sort((a, b) => a - b);

  return [...sorted.filter((time) => time >= first), ...sorted.filter((time) => time < first)];
}

// The −/+ step of a dose: half a tablet or a few drops of liquid at a time,
// whole units (UI, puffs, ampoules…) otherwise.
const HALF_STEP_UNITS = ['tablet', 'ml'];
const HALF = 0.5;

export function doseStep(doseUnit: string): number {
  return HALF_STEP_UNITS.includes(doseUnit) ? HALF : 1;
}

// "Cada N horas" from a first time: fixed clock times for the day, e.g. every
// 8 hours from 08:00 gives 08:00, 16:00 and 00:00.
export function everyHours(firstMinute: number, hours: number): number[] {
  const step = hours * MINUTES_PER_HOUR;
  const times: number[] = [];
  for (let offset = 0; offset < MINUTES_PER_DAY; offset += step) {
    times.push((firstMinute + offset) % MINUTES_PER_DAY);
  }

  return [...new Set(times)].sort((a, b) => a - b);
}

// The usual dosing intervals, in hours, as the frequency presets offer them.
export const INTERVALS = [
  { hours: 24, title: '1 vez al día' },
  { hours: 12, title: 'Cada 12 h' },
  { hours: 8, title: 'Cada 8 h' },
  { hours: 6, title: 'Cada 6 h' },
  { hours: 4, title: 'Cada 4 h' },
] as const;

const MORNING = 360;

// The preset a list of clock times follows, with the first dose from the
// morning on (every 8 hours from 08:00 is 00:00, 08:00 and 16:00); null when
// they follow none.
export function detectInterval(times: number[]): { hours: number; first: number } | null {
  for (const { hours } of INTERVALS) {
    const first = times.find((time) => time >= MORNING) ?? times[0];
    if (first !== undefined && everyHours(first, hours).join() === [...times].sort((a, b) => a - b).join()) {
      return { hours, first };
    }
  }

  return null;
}

const DAY_NAMES = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

function quantityText(quantity: number | null): string {
  return String(quantity ?? '').replace('.', ',');
}

function sentence(items: string[]): string {
  return items.length > 1 ? `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}` : items.join('');
}

// One line describing a dosage, as Medications::DosageSummary writes it on the
// server: "1 comprimido a las 08:00 y 20:00 (lun, mié, vie)".
export function dosageSummary(dosage: DosageForm, doseUnitLabel: string): string {
  const dose = `${quantityText(dosage.doseQuantity)} ${doseUnitLabel}`.trim();
  if (dosage.kind === 'as_needed') {
    return dosage.maxDosesPer24h ? `${dose} SOS, máximo ${dosage.maxDosesPer24h} en 24 horas` : `${dose} SOS`;
  }
  if (dosage.kind === 'single') return `${dose}, dosis única`;

  const times = `a las ${sentence(dosage.timesOfDay.map(minutesToTime))}`;
  const days = dosage.everyDay || dosage.weekdays.length === 0 ?
    '' :
    ` (${WEEKDAYS.filter((day) => dosage.weekdays.includes(day.value)).map((day) => DAY_NAMES[day.value]).join(', ')})`;

  return `${dose} ${times}${days}`;
}

// Local date-times as the form sends them: "2026-10-14T08:00", read by the
// server in the company time zone.
function pad(value: number): string {
  return String(value).padStart(TWO_DIGITS, '0');
}

export function toLocalDateTime(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function parseLocalDateTime(value: string): Date | null {
  const date = new Date(value);

  return value && !Number.isNaN(date.getTime()) ? date : null;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);

  return result;
}

// The scheduled doses of `days` days from the day of `from` on.
function scheduledDoses(dosages: DosageForm[], from: Date, days: number): Date[] {
  return dosages.filter((dosage) => dosage.kind === 'scheduled').flatMap((dosage) => (
    [...Array(days).keys()].flatMap((offset) => {
      const day = addDays(from, offset);
      if (!dosage.everyDay && dosage.weekdays.length > 0 && !dosage.weekdays.includes(day.getDay())) return [];

      return dosage.timesOfDay.map((minutes) => {
        const time = new Date(day);
        time.setHours(Math.floor(minutes / MINUTES_PER_HOUR), minutes % MINUTES_PER_HOUR, 0, 0);

        return time;
      });
    })
  ));
}

// The first scheduled dose at or after `from`, within a week.
export function nextDose(dosages: DosageForm[], from: Date): Date | null {
  const candidates = scheduledDoses(dosages, from, DAYS_PER_WEEK + 1).filter((time) => time >= from);

  return candidates.length > 0 ? new Date(Math.min(...candidates.map(Number))) : null;
}

const MAX_COURSE_DAYS = 366;

// How many doses a course with an end gives and when the last one is, to
// check "cada 8 h por 7 días" against the prescription. Single doses count
// once, at the start. Null for courses without scheduled doses or too long to
// be worth counting.
export function courseDoses(dosages: DosageForm[], start: Date, end: Date): { count: number; last: Date } | null {
  const days = Math.ceil((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
  if (days > MAX_COURSE_DAYS) return null;
  const doses = scheduledDoses(dosages, start, days).filter((time) => time >= start && time < end);
  if (doses.length === 0) return null;
  const singles = dosages.filter((dosage) => dosage.kind === 'single').length;

  return { count: doses.length + singles, last: new Date(Math.max(...doses.map(Number))) };
}

// "hoy 16:00", "mañana 08:00", "lun 20-10 08:00".
export function humanMoment(date: Date, now: Date = new Date()): string {
  const time = minutesToTime(date.getHours() * MINUTES_PER_HOUR + date.getMinutes());
  const days = Math.round((new Date(date).setHours(0, 0, 0, 0) - new Date(now).setHours(0, 0, 0, 0)) / MS_PER_DAY);
  if (days === 0) return `hoy ${time}`;
  if (days === 1) return `mañana ${time}`;

  return `${DAY_NAMES[date.getDay()]} ${pad(date.getDate())}-${pad(date.getMonth() + 1)} ${time}`;
}

// When an order starts and ends, as the form chooses it. "Ahora" leaves the
// start to the server; "por N días" ends N days after the start.
export interface PeriodChoice {
  startMode: 'now' | 'date';
  startsAt: string;
  durationMode: 'open' | 'days' | 'until';
  durationDays: number | null;
  endsAt: string;
}

// The chosen start, or null for "now".
export function periodStart(period: PeriodChoice): Date | null {
  return period.startMode === 'date' ? parseLocalDateTime(period.startsAt) : null;
}

export function periodEnd(period: PeriodChoice, now: Date = new Date()): Date | null {
  if (period.durationMode === 'days') {
    return period.durationDays ? addDays(periodStart(period) ?? now, period.durationDays) : null;
  }

  return period.durationMode === 'until' ? parseLocalDateTime(period.endsAt) : null;
}

export function startValid(period: PeriodChoice): boolean {
  return period.startMode === 'now' || !!periodStart(period);
}

// An end, when there is one, after the start.
export function durationValid(period: PeriodChoice, now: Date = new Date()): boolean {
  if (period.durationMode === 'open') return true;
  const end = periodEnd(period, now);

  return !!end && end > (periodStart(period) ?? now);
}

// `date` as the wall clock of `timeZone` reads it, in a Date whose local
// fields (getHours…) hold that reading: the form works in company time.
export function wallClock(date: Date, timeZone?: string): Date {
  if (!timeZone) return new Date(date);
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
  }).formatToParts(date).map((part) => [part.type, Number(part.value)]));

  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute);
}
